import React, { useEffect, useState } from 'react';
import api from '../../api/client';
import Button from '../../components/Button';
import FormField, { Input, Select } from '../../components/FormField';
import { buildTimetableEntries } from '../../utils/timetable';

function Toggle({ value, onChange }) {
  return (
    <div className="flex items-center gap-2 cursor-pointer select-none" onClick={() => onChange(!value)}>
      <span className="text-xs text-slate-500">{value ? 'Für Teilnehmer sichtbar' : 'Nur für Admins sichtbar'}</span>
      <div className={`relative w-10 h-5 rounded-full transition-colors ${value ? 'bg-emerald-500' : 'bg-slate-300'}`}>
        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${value ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </div>
    </div>
  );
}

const TYPE_BADGE = {
  lan: 'bg-sand-50 text-sand-700 border-sand-200',
  tournament: 'bg-ocean-50 text-ocean-700 border-ocean-200',
  custom: 'bg-purple-50 text-purple-700 border-purple-200',
};
const TYPE_LABEL = { lan: 'LAN', tournament: 'Turnier', custom: 'Eintrag' };

const fmt = (d) => d ? new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '–';

export default function AdminTimetable() {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [event, setEvent] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [customEntries, setCustomEntries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newTime, setNewTime] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => { api.get('/api/events/all').then((r) => setEvents(r.data)); }, []);

  const loadCustomEntries = (eventId) => {
    api.get(`/api/events/${eventId}/timetable-entries`).then((r) => setCustomEntries(r.data));
  };

  useEffect(() => {
    if (!selectedEvent) { setEvent(null); setTournaments([]); setCustomEntries([]); return; }
    api.get(`/api/events/${selectedEvent}`).then((r) => setEvent(r.data));
    api.get(`/api/events/${selectedEvent}/tournaments`).then((r) => setTournaments(r.data));
    loadCustomEntries(selectedEvent);
  }, [selectedEvent]);

  const toggleVisible = async (value) => {
    setSaving(true); setError('');
    try {
      const res = await api.put(`/api/events/${selectedEvent}`, { timetable_visible: value });
      setEvent(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  const addEntry = async (e) => {
    e.preventDefault();
    if (!newLabel || !newTime) return;
    setAdding(true); setError('');
    try {
      await api.post('/api/admin/timetable-entries', {
        event_id: parseInt(selectedEvent),
        label: newLabel,
        time: newTime,
      });
      setNewLabel(''); setNewTime('');
      loadCustomEntries(selectedEvent);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Anlegen');
    } finally { setAdding(false); }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Eintrag wirklich löschen?')) return;
    await api.delete(`/api/admin/timetable-entries/${id}`);
    loadCustomEntries(selectedEvent);
  };

  const entries = event ? buildTimetableEntries(event, tournaments, customEntries) : [];

  return (
    <div>
      <h2 className="page-title">Timetable</h2>

      <FormField label="Event auswählen" className="max-w-sm mb-6">
        <Select value={selectedEvent} onChange={(e) => setSelectedEvent(e.target.value)}>
          <option value="">– Event wählen –</option>
          {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
        </Select>
      </FormField>

      {event && (
        <>
          <div className="card flex flex-wrap items-center justify-between gap-3 mb-6">
            <div>
              <p className="font-semibold text-ocean-900">{event.name}</p>
              <p className="text-xs text-slate-500 mt-0.5">
                Sichtbar unter „Timetable" im Event für alle Teilnehmer, wenn aktiviert.
              </p>
            </div>
            <Toggle value={event.timetable_visible} onChange={toggleVisible} />
          </div>

          {error && <div className="alert-error mb-4">{error}</div>}
          {saving && <p className="text-xs text-slate-400 mb-2">Speichere...</p>}

          <div className="card mb-6">
            <h3 className="section-title">Eintrag hinzufügen</h3>
            <form onSubmit={addEntry} className="flex flex-wrap items-end gap-3">
              <FormField label="Bezeichnung" className="mb-0 flex-1 min-w-[180px]">
                <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="z.B. Frühstück, Siegerehrung..." required />
              </FormField>
              <FormField label="Datum & Uhrzeit" className="mb-0">
                <Input type="datetime-local" value={newTime} onChange={(e) => setNewTime(e.target.value)} required />
              </FormField>
              <Button type="submit" loading={adding}>Hinzufügen</Button>
            </form>
          </div>

          {entries.length === 0 ? (
            <div className="card text-center py-10 text-slate-400 text-sm">
              Noch keine Termine hinterlegt.
            </div>
          ) : (
            <div className="card">
              <div className="space-y-2">
                {entries.map((e, i) => (
                  <div key={i} className="flex items-center justify-between border-b border-slate-100 last:border-0 py-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${TYPE_BADGE[e.type]}`}>
                        {TYPE_LABEL[e.type]}
                      </span>
                      <span className="font-medium text-slate-800">{e.label}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-slate-500 font-medium">{fmt(e.time)}</span>
                      {e.type === 'custom' && (
                        <Button variant="danger" size="sm" onClick={() => deleteEntry(e.id)}>Löschen</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
